<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  formatCompactNumber,
  markedTextSegments,
} from '../utils/reportFormatters';

// Coluna lateral do bloco de performance: anúncios publicados/tráfego e o
// insight narrativo sobre a conversão do indicador selecionado.
const props = defineProps({
  sectionDescription: { type: String, default: '' },
  portfolio: { type: Object, default: () => ({}) },
  narratives: { type: Array, default: () => [] },
  metricSubject: { type: String, default: '' },
  metricVerb: { type: String, default: '' },
  proposalOperationLabel: { type: String, default: '' },
});

const performanceInsightLabel = computed(() => {
  const delta = Number(props.portfolio.offer_conversion_delta || 0);
  if (delta > 0) return 'Conversão acima da média';
  if (delta < 0) return 'Conversão abaixo da média';
  return 'Conversão dentro da média';
});

const performanceInsightText = computed(() => {
  const conversion = Number(props.portfolio.offer_conversion || 0);
  const delta = Math.abs(Number(props.portfolio.offer_conversion_delta || 0));
  const conversionDelta = Number(props.portfolio.offer_conversion_delta || 0);
  let direction = 'dentro';
  if (conversionDelta > 0) {
    direction = 'acima';
  } else if (conversionDelta < 0) {
    direction = 'abaixo';
  }

  return `No período analisado, ${props.metricSubject} ${props.metricVerb} uma conversão de propostas de ${props.proposalOperationLabel} de ${conversion}% (${delta}% ${direction} do valor de referência). A conversão é a relação entre propostas enviadas e aceitas, e o valor de referência é calculado com base em dados do mercado e conhecimento exclusivo da Imudei para sugerir as melhores conversões para a sua imobiliária.`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-6 content-start">
    <LaisCard title="Anúncios Publicados">
      <p class="text-[13px] leading-relaxed text-lais-text-body">
        {{ sectionDescription }}
      </p>
      <div class="mt-4 grid gap-3">
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p class="text-[12px] font-semibold text-lais-text-muted">
            Anúncios publicados (até a data)
          </p>
          <p class="mt-2 text-[24px] font-semibold text-lais-text">
            {{ portfolio.published_listings }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p class="text-[12px] font-semibold text-lais-text-muted">
            Tráfego médio no período
          </p>
          <p class="mt-2 text-[24px] font-semibold text-lais-text">
            {{ formatCompactNumber(portfolio.traffic_average_per_week) }}/semana
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            Tráfego (acessos na Imudei): {{ portfolio.traffic }}
          </p>
        </div>
      </div>
    </LaisCard>

    <LaisCard title="Insights sobre este indicador">
      <p class="text-[13px] font-semibold text-lais-primary">
        {{ performanceInsightLabel }}
      </p>
      <p class="mt-2 text-[13px] leading-relaxed text-lais-text-body">
        {{ performanceInsightText }}
      </p>
      <ul v-if="narratives.length" class="mt-4 grid gap-2">
        <li
          v-for="narrative in narratives"
          :key="narrative"
          class="text-[12px] leading-relaxed text-lais-text-body"
        >
          <template
            v-for="(segment, index) in markedTextSegments(narrative)"
            :key="`${narrative}-${index}`"
          >
            <strong v-if="segment.bold" class="font-semibold text-lais-text">
              {{ segment.text }}
            </strong>
            <template v-else>{{ segment.text }}</template>
          </template>
        </li>
      </ul>
    </LaisCard>
  </div>
</template>
